import { useEffect } from "react";

import { useMutation } from "@tanstack/react-query";

import {
  useAuthActions,
  useAuthStore,
  useIsScopedTo,
} from "src/store/AuthStore";

export type WorkspaceScopingStatus = "ready" | "pending" | "error";

type ScopeVariables = {
  orgId: string;
  wsId: string;
  signal: AbortSignal;
};

/**
 * Mints a token scoped to (orgId, wsId) for a workspace route, returning
 * "pending" while minting and "error" on failure/scope-mismatch.
 */
export const useWorkspaceScoping = (
  orgId: string | undefined,
  wsId: string | undefined,
): WorkspaceScopingStatus => {
  const isScoped = useIsScopedTo(orgId, wsId);
  const { setSessionScope } = useAuthActions();

  const needsScope = !!orgId && !!wsId && !isScoped;

  const { mutate, isError, variables } = useMutation({
    mutationFn: async ({ orgId, wsId, signal }: ScopeVariables) => {
      await setSessionScope(orgId, wsId, { signal });
      // don't apply/validate against a stale scope
      if (signal.aborted) {
        return;
      }

      const { scope_org_id, scope_ws_id } = useAuthStore.getState();
      if (scope_org_id !== orgId || scope_ws_id !== wsId) {
        throw new Error("Minted token scope does not match the workspace");
      }
    },
  });

  useEffect(() => {
    if (!needsScope || !orgId || !wsId) {
      return;
    }
    const controller = new AbortController();
    mutate({ orgId, wsId, signal: controller.signal });
    return () => controller.abort();
  }, [needsScope, orgId, wsId, mutate]);

  if (needsScope) {
    if (isError && variables?.orgId === orgId && variables?.wsId === wsId) {
      return "error";
    }
    return "pending";
  }
  return "ready";
};
